import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";
import { sanitizeStorageFileName } from "@/utils/storage";
import {
	SectionKey,
	SectionConfigMap,
	HeaderSectionConfig,
	ContactSectionConfig,
	SummarySectionConfig,
	ExperienceSectionConfig,
	SkillsSectionConfig,
	CustomSectionConfig,
} from "@/types/cvTemplate";

interface CvSectionConfigPanelProps {
	sectionKey: SectionKey;
	config: SectionConfigMap[SectionKey];
	onConfigChange: (config: SectionConfigMap[SectionKey]) => void;
	disabled?: boolean;
}

export function CvSectionConfigPanel({ sectionKey, config, onConfigChange, disabled }: CvSectionConfigPanelProps) {
	const { t } = useTranslation();

	switch (sectionKey) {
		case "header":
			return <HeaderConfig config={config as HeaderSectionConfig} onChange={onConfigChange} disabled={disabled} />;
		case "contact":
			return <ContactConfig config={config as ContactSectionConfig} onChange={onConfigChange} disabled={disabled} />;
		case "summary":
			return <SummaryConfig config={config as SummarySectionConfig} onChange={onConfigChange} disabled={disabled} />;
		case "experience":
			return <ExperienceConfig config={config as ExperienceSectionConfig} onChange={onConfigChange} disabled={disabled} />;
		case "skills":
			return <SkillsConfig config={config as SkillsSectionConfig} onChange={onConfigChange} disabled={disabled} />;
		case "custom":
			return <CustomConfig config={config as CustomSectionConfig} onChange={onConfigChange} disabled={disabled} />;
		default:
			return null;
	}
}

// ── Per-section config forms ──

function ConfigRow({ children }: { children: React.ReactNode }) {
	return <div className="flex items-center justify-between py-1.5">{children}</div>;
}

function HeaderConfig({ config, onChange, disabled }: { config: HeaderSectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const { currentOrganization } = useAuth();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [uploading, setUploading] = useState(false);

	const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		if (!file.type.startsWith("image/")) {
			toast({ title: "Invalid file", description: "Please select an image file (PNG, JPG, SVG)", variant: "destructive" });
			return;
		}
		if (file.size > 2 * 1024 * 1024) {
			toast({ title: "File too large", description: "Logo must be under 2MB", variant: "destructive" });
			return;
		}

		setUploading(true);
		try {
			const orgId = currentOrganization?.id || "default";
			const path = `templates/logos/${orgId}/${Date.now()}_${sanitizeStorageFileName(file.name)}`;
			const { error } = await supabase.storage
				.from("cvs")
				.upload(path, file, { upsert: true, contentType: file.type });

			if (error) throw error;

			const { data: { publicUrl } } = supabase.storage
				.from("cvs")
				.getPublicUrl(path);

			onChange({ ...config, showLogo: true, logoUrl: publicUrl });
		} catch (err: any) {
			toast({ title: "Upload failed", description: err.message || "Could not upload logo", variant: "destructive" });
		} finally {
			setUploading(false);
			if (fileInputRef.current) fileInputRef.current.value = "";
		}
	};

	return (
		<div className="space-y-2">
			{/* Anonymous mode */}
			<ConfigRow>
				<Label className="text-xs">{t("admin.cvBuilder.anonymous", "Anonymous CV (hide name)")}</Label>
				<Switch
					checked={config.anonymous || false}
					onCheckedChange={(anonymous) => onChange({ ...config, anonymous })}
					disabled={disabled}
				/>
			</ConfigRow>

			{/* Logo */}
			<ConfigRow>
				<Label className="text-xs">{t("admin.cvBuilder.showLogo", "Show organization logo")}</Label>
				<Switch
					checked={config.showLogo}
					onCheckedChange={(showLogo) => onChange({ ...config, showLogo })}
					disabled={disabled}
				/>
			</ConfigRow>
			{config.showLogo && (
				<div className="space-y-2">
					{/* Logo preview + upload */}
					<div className="flex items-center gap-3">
						{config.logoUrl ? (
							<div className="relative group">
								<img
									src={config.logoUrl}
									alt="Logo"
									className="h-10 max-w-[120px] object-contain rounded border bg-white p-1"
								/>
								<button
									type="button"
									onClick={() => onChange({ ...config, logoUrl: "" })}
									className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-sm p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
								>
									<X className="h-3 w-3" />
								</button>
							</div>
						) : (
							<div className="h-10 w-20 rounded border border-dashed flex items-center justify-center text-muted-foreground">
								<ImageIcon className="h-4 w-4" />
							</div>
						)}
						<div className="flex-1">
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleLogoUpload}
								className="hidden"
								disabled={disabled || uploading}
							/>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => fileInputRef.current?.click()}
								disabled={disabled || uploading}
								className="h-7 text-xs"
							>
								{uploading
									? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading...</>
									: <><Upload className="h-3 w-3 mr-1" /> Upload logo</>
								}
							</Button>
						</div>
					</div>
					{/* URL fallback */}
					<Input
						value={config.logoUrl || ""}
						onChange={(e) => onChange({ ...config, logoUrl: e.target.value })}
						placeholder={t("admin.cvBuilder.logoUrlPlaceholder", "Or paste a URL...")}
						className="h-7 text-xs"
						disabled={disabled}
					/>
				</div>
			)}

			{/* Name style */}
			{!config.anonymous && (
				<ConfigRow>
					<Label className="text-xs">{t("admin.cvBuilder.nameStyle", "Name style")}</Label>
					<Select
						value={config.nameStyle}
						onValueChange={(nameStyle) => onChange({ ...config, nameStyle })}
						disabled={disabled}
					>
						<SelectTrigger className="w-32 h-8 text-xs">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="bold">Bold</SelectItem>
							<SelectItem value="large">Large</SelectItem>
							<SelectItem value="uppercase">Uppercase</SelectItem>
						</SelectContent>
					</Select>
				</ConfigRow>
			)}
		</div>
	);
}

function ContactConfig({ config, onChange, disabled }: { config: ContactSectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	const fields = [
		{ key: "showEmail" as const, label: t("admin.cvBuilder.showEmail", "Email") },
		{ key: "showPhone" as const, label: t("admin.cvBuilder.showPhone", "Phone") },
		{ key: "showLocation" as const, label: t("admin.cvBuilder.showLocation", "Location") },
		{ key: "showLinkedin" as const, label: t("admin.cvBuilder.showLinkedin", "LinkedIn") },
	];

	return (
		<div className="space-y-1">
			{fields.map(f => (
				<ConfigRow key={f.key}>
					<Label className="text-xs">{f.label}</Label>
					<Switch
						checked={config[f.key]}
						onCheckedChange={(val) => onChange({ ...config, [f.key]: val })}
						disabled={disabled}
					/>
				</ConfigRow>
			))}
		</div>
	);
}

function SummaryConfig({ config, onChange, disabled }: { config: SummarySectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	return (
		<div className="space-y-2">
			<ConfigRow>
				<Label className="text-xs">{t("admin.cvBuilder.summarySource", "Source")}</Label>
				<Select
					value={config.source}
					onValueChange={(source) => onChange({ ...config, source })}
					disabled={disabled}
				>
					<SelectTrigger className="w-40 h-8 text-xs">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="ai">{t("admin.cvBuilder.aiSynthesis", "AI Synthesis")}</SelectItem>
						<SelectItem value="custom">{t("admin.cvBuilder.customText", "Custom Text")}</SelectItem>
					</SelectContent>
				</Select>
			</ConfigRow>
			{config.source === "custom" && (
				<Textarea
					value={config.customText || ""}
					onChange={(e) => onChange({ ...config, customText: e.target.value })}
					placeholder={t("admin.cvBuilder.customTextPlaceholder", "Enter a default summary text...")}
					className="text-xs min-h-[80px]"
					disabled={disabled}
				/>
			)}
		</div>
	);
}

function ExperienceConfig({ config, onChange, disabled }: { config: ExperienceSectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	return (
		<div className="space-y-1">
			<ConfigRow>
				<Label className="text-xs">{t("admin.cvBuilder.showIndustry", "Show industry")}</Label>
				<Switch
					checked={config.showIndustry}
					onCheckedChange={(showIndustry) => onChange({ ...config, showIndustry })}
					disabled={disabled}
				/>
			</ConfigRow>
			<ConfigRow>
				<Label className="text-xs">{t("admin.cvBuilder.showDateRange", "Show date range")}</Label>
				<Switch
					checked={config.showDateRange}
					onCheckedChange={(showDateRange) => onChange({ ...config, showDateRange })}
					disabled={disabled}
				/>
			</ConfigRow>
		</div>
	);
}

function SkillsConfig({ config, onChange, disabled }: { config: SkillsSectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	return (
		<ConfigRow>
			<Label className="text-xs">{t("admin.cvBuilder.skillsLayout", "Layout")}</Label>
			<Select
				value={config.layout}
				onValueChange={(layout) => onChange({ ...config, layout })}
				disabled={disabled}
			>
				<SelectTrigger className="w-32 h-8 text-xs">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="badges">{t("admin.cvBuilder.badgesLayout", "Badges")}</SelectItem>
					<SelectItem value="list">{t("admin.cvBuilder.listLayout", "List")}</SelectItem>
				</SelectContent>
			</Select>
		</ConfigRow>
	);
}

function CustomConfig({ config, onChange, disabled }: { config: CustomSectionConfig; onChange: (c: any) => void; disabled?: boolean }) {
	const { t } = useTranslation();
	return (
		<div className="space-y-2">
			<div className="space-y-1">
				<Label className="text-xs">{t("admin.cvBuilder.sectionTitle", "Section title")}</Label>
				<Input
					value={config.title || ""}
					onChange={(e) => onChange({ ...config, title: e.target.value })}
					placeholder={t("admin.cvBuilder.sectionTitlePlaceholder", "e.g. Certifications, Languages...")}
					className="h-8 text-xs"
					disabled={disabled}
				/>
			</div>
			<div className="space-y-1">
				<Label className="text-xs">{t("admin.cvBuilder.sectionContent", "Content")}</Label>
				<Textarea
					value={config.content || ""}
					onChange={(e) => onChange({ ...config, content: e.target.value })}
					placeholder={t("admin.cvBuilder.sectionContentPlaceholder", "Enter content for this section...")}
					className="text-xs min-h-[80px]"
					disabled={disabled}
				/>
			</div>
		</div>
	);
}
